import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import ua, {TranslationKey} from "./ua";
import en from "./en";
import {PHRASES_EN} from "./phrases";

export type Lang = "ua" | "en";

const STORAGE_KEY = "askold-lang";
const DICTS: Record<Lang, Record<TranslationKey, string>> = { ua, en };

const readLang = (): Lang => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        return saved === "en" ? "en" : "ua";
    } catch {
        return "ua";
    }
};

interface II18n {
    lang: Lang,
    setLang: (lang: Lang) => void,
    // переклад за ключем; {n}-підстановки передаються другим аргументом
    t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
    // переклад довільного тексту (з бази / масивів), якщо його немає у словнику — повертається як є
    tr: (text: string) => string,
}

const I18nContext = createContext<II18n>({
    lang: "ua",
    setLang: () => {},
    t: (key) => ua[key],
    tr: (text) => text,
});

export const I18nProvider = ({ children }: { children: ReactNode }) => {
    const [lang, setLangState] = useState<Lang>(readLang);

    useEffect(() => {
        document.documentElement.lang = lang === "ua" ? "uk" : "en";
    }, [lang]);

    const setLang = useCallback((next: Lang) => {
        setLangState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // без localStorage мова просто не збережеться між візитами
        }
    }, []);

    const value = useMemo<II18n>(() => ({
        lang,
        setLang,
        t: (key, vars) => {
            let text = DICTS[lang][key] ?? ua[key] ?? key;
            if (vars) {
                Object.entries(vars).forEach(([name, val]) => {
                    text = text.replace(`{${name}}`, String(val));
                });
            }
            return text;
        },
        tr: (text) => (lang === "en" ? PHRASES_EN[text] ?? text : text),
    }), [lang, setLang]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
